type LottieVector = number[];
type LottieKeyframe = { t: number; s: LottieVector };

export type LottieAnimationData = Record<string, unknown>;

export interface LottiePresetDefinition {
  id: string;
  name: string;
  vertical: "dental" | "yoga" | "restaurant" | "generic";
  category: string;
  description: string;
  animationData: LottieAnimationData;
}

const TOKEN_PREFIX = "ease-lottie-preset:";
const FRAME_RATE = 60;
const IN_POINT = 0;
const OUT_POINT = 96;
const CANVAS_SIZE = 240;
const CENTER = CANVAS_SIZE / 2;

function hexToRgba(hex: string): [number, number, number, number] {
  const normalized = hex.replace("#", "");
  const value = normalized.length === 3
    ? normalized.split("").map((part) => `${part}${part}`).join("")
    : normalized;

  const red = Number.parseInt(value.slice(0, 2), 16) / 255;
  const green = Number.parseInt(value.slice(2, 4), 16) / 255;
  const blue = Number.parseInt(value.slice(4, 6), 16) / 255;
  return [red, green, blue, 1];
}

function animatedValue(values: LottieKeyframe[]) {
  return {
    a: 1,
    k: values.map((entry) => ({ t: entry.t, s: [...entry.s] })),
  };
}

function staticValue(value: number | number[]) {
  return { a: 0, k: value };
}

function ellipseShape(size: [number, number], color: [number, number, number, number]) {
  return {
    ty: "gr",
    it: [
      {
        ty: "el",
        p: staticValue([0, 0]),
        s: staticValue(size),
        nm: "Ellipse Path",
      },
      {
        ty: "fl",
        c: staticValue(color),
        o: staticValue(100),
        r: 1,
        nm: "Fill",
      },
      {
        ty: "tr",
        p: staticValue([0, 0]),
        a: staticValue([0, 0]),
        s: staticValue([100, 100]),
        r: staticValue(0),
        o: staticValue(100),
        sk: staticValue(0),
        sa: staticValue(0),
        nm: "Transform",
      },
    ],
    nm: "Ellipse Group",
    np: 3,
    cix: 2,
    bm: 0,
  };
}

function rectShape(
  size: [number, number],
  color: [number, number, number, number],
  roundness = 24,
) {
  return {
    ty: "gr",
    it: [
      {
        ty: "rc",
        p: staticValue([0, 0]),
        s: staticValue(size),
        r: staticValue(roundness),
        nm: "Rect Path",
      },
      {
        ty: "fl",
        c: staticValue(color),
        o: staticValue(100),
        r: 1,
        nm: "Fill",
      },
      {
        ty: "tr",
        p: staticValue([0, 0]),
        a: staticValue([0, 0]),
        s: staticValue([100, 100]),
        r: staticValue(0),
        o: staticValue(100),
        sk: staticValue(0),
        sa: staticValue(0),
        nm: "Transform",
      },
    ],
    nm: "Rect Group",
    np: 3,
    cix: 2,
    bm: 0,
  };
}

function buildLayer(
  name: string,
  shapes: unknown[],
  position: LottieKeyframe[] | LottieVector,
  scale: LottieKeyframe[] | LottieVector,
  opacity?: LottieKeyframe[] | number,
  rotation?: LottieKeyframe[] | number,
) {
  const isKeyframed = (value: LottieKeyframe[] | LottieVector | number): value is LottieKeyframe[] =>
    Array.isArray(value) && value.length > 0 && typeof value[0] === "object";
  const resolveAnimatedValue = (
    value: LottieKeyframe[] | LottieVector | number | undefined,
    fallback: number | number[],
  ) => {
    if (typeof value === "number") {
      return staticValue(value);
    }

    if (value && isKeyframed(value)) {
      return animatedValue(value);
    }

    if (Array.isArray(value)) {
      return staticValue(value);
    }

    return staticValue(fallback);
  };

  return {
    ddd: 0,
    ind: 1,
    ty: 4,
    nm: name,
    sr: 1,
    ks: {
      o: resolveAnimatedValue(opacity, 100),
      r: resolveAnimatedValue(rotation, 0),
      p: resolveAnimatedValue(position, [CENTER, CENTER, 0]),
      a: staticValue([0, 0, 0]),
      s: resolveAnimatedValue(scale, [100, 100, 100]),
    },
    ao: 0,
    shapes,
    ip: IN_POINT,
    op: OUT_POINT,
    st: 0,
    bm: 0,
  };
}

function baseDocument(layers: unknown[]): LottieAnimationData {
  return {
    v: "5.12.2",
    fr: FRAME_RATE,
    ip: IN_POINT,
    op: OUT_POINT,
    w: CANVAS_SIZE,
    h: CANVAS_SIZE,
    nm: "Ease Lottie Preset",
    ddd: 0,
    assets: [],
    layers,
  };
}

function createPulseOrb(primary: string, accent: string): LottieAnimationData {
  return baseDocument([
    buildLayer(
      "Orb",
      [
        ellipseShape([124, 124], hexToRgba(primary)),
        ellipseShape([64, 64], hexToRgba(accent)),
      ],
      [CENTER, CENTER, 0],
      [
        { t: 0, s: [82, 82, 100] },
        { t: 32, s: [106, 106, 100] },
        { t: 64, s: [84, 84, 100] },
        { t: 96, s: [82, 82, 100] },
      ],
    ),
  ]);
}

function createBloom(primary: string, accent: string): LottieAnimationData {
  return baseDocument([
    buildLayer(
      "Petal Top",
      [ellipseShape([46, 94], hexToRgba(primary))],
      [CENTER, 62, 0],
      [
        { t: 0, s: [70, 70, 100] },
        { t: 40, s: [100, 100, 100] },
        { t: 96, s: [70, 70, 100] },
      ],
    ),
    buildLayer(
      "Petal Left",
      [ellipseShape([46, 94], hexToRgba(accent))],
      [70, CENTER + 6, 0],
      [
        { t: 8, s: [72, 72, 100] },
        { t: 44, s: [102, 102, 100] },
        { t: 96, s: [72, 72, 100] },
      ],
      100,
      32,
    ),
    buildLayer(
      "Petal Right",
      [ellipseShape([46, 94], hexToRgba(accent))],
      [170, CENTER + 6, 0],
      [
        { t: 12, s: [72, 72, 100] },
        { t: 48, s: [102, 102, 100] },
        { t: 96, s: [72, 72, 100] },
      ],
      100,
      -32,
    ),
    buildLayer(
      "Core",
      [ellipseShape([44, 44], hexToRgba(primary))],
      [CENTER, CENTER + 20, 0],
      [
        { t: 0, s: [80, 80, 100] },
        { t: 48, s: [112, 112, 100] },
        { t: 96, s: [80, 80, 100] },
      ],
    ),
  ]);
}

function createSteam(primary: string, accent: string): LottieAnimationData {
  return baseDocument([
    buildLayer(
      "Steam Left",
      [rectShape([20, 78], hexToRgba(primary), 20)],
      [
        { t: 0, s: [82, 158, 0] },
        { t: 48, s: [72, 108, 0] },
        { t: 96, s: [82, 158, 0] },
      ],
      [100, 100, 100],
      [
        { t: 0, s: [30] },
        { t: 28, s: [100] },
        { t: 64, s: [24] },
        { t: 96, s: [30] },
      ],
    ),
    buildLayer(
      "Steam Mid",
      [rectShape([22, 92], hexToRgba(accent), 20)],
      [
        { t: 0, s: [120, 166, 0] },
        { t: 48, s: [120, 98, 0] },
        { t: 96, s: [120, 166, 0] },
      ],
      [100, 100, 100],
      [
        { t: 0, s: [28] },
        { t: 36, s: [100] },
        { t: 72, s: [22] },
        { t: 96, s: [28] },
      ],
    ),
    buildLayer(
      "Steam Right",
      [rectShape([18, 72], hexToRgba(primary), 18)],
      [
        { t: 0, s: [158, 156, 0] },
        { t: 48, s: [168, 112, 0] },
        { t: 96, s: [158, 156, 0] },
      ],
      [100, 100, 100],
      [
        { t: 0, s: [22] },
        { t: 24, s: [92] },
        { t: 68, s: [18] },
        { t: 96, s: [22] },
      ],
    ),
    buildLayer(
      "Plate",
      [rectShape([152, 22], hexToRgba(accent), 22)],
      [CENTER, 188, 0],
      [100, 100, 100],
    ),
  ]);
}

function createLoadingDots(primary: string, accent: string): LottieAnimationData {
  return baseDocument([
    buildLayer(
      "Dot Left",
      [ellipseShape([28, 28], hexToRgba(primary))],
      [76, CENTER, 0],
      [100, 100, 100],
      [
        { t: 0, s: [25] },
        { t: 18, s: [100] },
        { t: 36, s: [25] },
        { t: 96, s: [25] },
      ],
    ),
    buildLayer(
      "Dot Mid",
      [ellipseShape([28, 28], hexToRgba(accent))],
      [CENTER, CENTER, 0],
      [100, 100, 100],
      [
        { t: 12, s: [25] },
        { t: 30, s: [100] },
        { t: 48, s: [25] },
        { t: 96, s: [25] },
      ],
    ),
    buildLayer(
      "Dot Right",
      [ellipseShape([28, 28], hexToRgba(primary))],
      [164, CENTER, 0],
      [100, 100, 100],
      [
        { t: 24, s: [25] },
        { t: 42, s: [100] },
        { t: 60, s: [25] },
        { t: 96, s: [25] },
      ],
    ),
  ]);
}

type BuilderKind = "pulse" | "bloom" | "steam" | "dots";

interface PresetMotif {
  slug: string;
  name: string;
  description: string;
  builder: BuilderKind;
  category: "hero" | "trust" | "utility" | "status";
}

interface VerticalTone {
  slug: string;
  name: string;
  primary: string;
  accent: string;
}

interface VerticalPresetBlueprint {
  vertical: LottiePresetDefinition["vertical"];
  tones: VerticalTone[];
  motifs: PresetMotif[];
}

const BUILDERS: Record<BuilderKind, (primary: string, accent: string) => LottieAnimationData> = {
  pulse: createPulseOrb,
  bloom: createBloom,
  steam: createSteam,
  dots: createLoadingDots,
};

const SHARED_MOTIFS: PresetMotif[] = [
  { slug: "glow", name: "Glow", description: "Hero accent that adds instant motion-led polish.", builder: "pulse", category: "hero" },
  { slug: "halo", name: "Halo", description: "Soft premium pulse for first-impression sections.", builder: "pulse", category: "hero" },
  { slug: "signal", name: "Signal", description: "Confidence-building motion for conversion blocks.", builder: "pulse", category: "trust" },
  { slug: "bloom", name: "Bloom", description: "Layered reveal for storytelling and testimonial sections.", builder: "bloom", category: "hero" },
  { slug: "petal", name: "Petal", description: "Organic reveal animation for softer section transitions.", builder: "bloom", category: "trust" },
  { slug: "flare", name: "Flare", description: "Premium flourish for featured offers and highlights.", builder: "bloom", category: "trust" },
  { slug: "rise", name: "Rise", description: "Vertical motion for sections that need a sense of lift.", builder: "steam", category: "utility" },
  { slug: "stream", name: "Stream", description: "Gentle upward motion for atmosphere and pacing.", builder: "steam", category: "utility" },
  { slug: "drift", name: "Drift", description: "Ambient motion accent for media and side panels.", builder: "steam", category: "utility" },
  { slug: "plume", name: "Plume", description: "Statement motion for hero illustrations and callouts.", builder: "steam", category: "hero" },
  { slug: "dots", name: "Dots", description: "Default waiting/loading loop for inline states.", builder: "dots", category: "status" },
  { slug: "beats", name: "Beats", description: "Livelier status rhythm for confirmation and progress.", builder: "dots", category: "status" },
  { slug: "queue", name: "Queue", description: "Short looping utility motion for async states.", builder: "dots", category: "status" },
];

const PRESET_BLUEPRINTS: VerticalPresetBlueprint[] = [
  {
    vertical: "dental",
    tones: [
      { slug: "calm", name: "Calm", primary: "#38BDF8", accent: "#E0F2FE" },
      { slug: "clean", name: "Clean", primary: "#0EA5E9", accent: "#7DD3FC" },
      { slug: "clinical", name: "Clinical", primary: "#93C5FD", accent: "#DBEAFE" },
      { slug: "midnight", name: "Midnight", primary: "#1D4ED8", accent: "#BFDBFE" },
    ],
    motifs: SHARED_MOTIFS.map((motif) => ({
      ...motif,
      slug: `tooth-${motif.slug}`,
      name: `Tooth ${motif.name}`,
      description: `${motif.description} Tuned for dental trust, reassurance, and precision.`,
    })),
  },
  {
    vertical: "yoga",
    tones: [
      { slug: "sage", name: "Sage", primary: "#84CC16", accent: "#DCFCE7" },
      { slug: "lotus", name: "Lotus", primary: "#65A30D", accent: "#BEF264" },
      { slug: "forest", name: "Forest", primary: "#4D7C0F", accent: "#D9F99D" },
      { slug: "dusk", name: "Dusk", primary: "#16A34A", accent: "#BBF7D0" },
    ],
    motifs: SHARED_MOTIFS.map((motif) => ({
      ...motif,
      slug: `flow-${motif.slug}`,
      name: `Flow ${motif.name}`,
      description: `${motif.description} Tuned for breath, calm, and mindful movement.`,
    })),
  },
  {
    vertical: "restaurant",
    tones: [
      { slug: "amber", name: "Amber", primary: "#F59E0B", accent: "#FEF3C7" },
      { slug: "copper", name: "Copper", primary: "#D97706", accent: "#FDE68A" },
      { slug: "ember", name: "Ember", primary: "#C2410C", accent: "#FDBA74" },
      { slug: "midnight", name: "Midnight", primary: "#92400E", accent: "#FCD34D" },
    ],
    motifs: SHARED_MOTIFS.map((motif) => ({
      ...motif,
      slug: `plate-${motif.slug}`,
      name: `Plate ${motif.name}`,
      description: `${motif.description} Tuned for warmth, appetite, and hospitality.`,
    })),
  },
  {
    vertical: "generic",
    tones: [
      { slug: "violet", name: "Violet", primary: "#8B5CF6", accent: "#EDE9FE" },
      { slug: "orchid", name: "Orchid", primary: "#A855F7", accent: "#DDD6FE" },
      { slug: "iris", name: "Iris", primary: "#7C3AED", accent: "#C4B5FD" },
      { slug: "indigo", name: "Indigo", primary: "#4338CA", accent: "#C7D2FE" },
    ],
    motifs: SHARED_MOTIFS.map((motif) => ({
      ...motif,
      slug: `signal-${motif.slug}`,
      name: `Signal ${motif.name}`,
      description: `${motif.description} Tuned as a general-purpose premium SaaS motion accent.`,
    })),
  },
];

export const LOTTIE_PRESETS: LottiePresetDefinition[] = PRESET_BLUEPRINTS.flatMap((group) =>
  group.tones.flatMap((tone) =>
    group.motifs.map((motif) => ({
      id: `${motif.slug}-${tone.slug}`,
      name: `${motif.name} ${tone.name}`,
      vertical: group.vertical,
      category: `${group.vertical}-${motif.category}`,
      description: `${motif.description} ${tone.name} colorway.`,
      animationData: BUILDERS[motif.builder](tone.primary, tone.accent),
    })),
  ),
);

const PRESET_MAP = new Map(LOTTIE_PRESETS.map((preset) => [preset.id, preset]));

export function createLottiePresetToken(presetId: string): string {
  return `${TOKEN_PREFIX}${presetId}`;
}

export function isLottiePresetToken(value: string | null | undefined): boolean {
  return typeof value === "string" && value.startsWith(TOKEN_PREFIX);
}

export function getLottiePresetIdFromToken(value: string | null | undefined): string | null {
  if (typeof value !== "string" || !isLottiePresetToken(value)) {
    return null;
  }

  return value.slice(TOKEN_PREFIX.length) || null;
}

export function getLottiePresetById(presetId: string | null | undefined): LottiePresetDefinition | null {
  if (!presetId) {
    return null;
  }

  return PRESET_MAP.get(presetId) ?? null;
}

export function resolveLottiePresetToken(value: string | null | undefined): LottiePresetDefinition | null {
  return getLottiePresetById(getLottiePresetIdFromToken(value));
}

export function listLottiePresets(
  vertical?: LottiePresetDefinition["vertical"] | "all",
): LottiePresetDefinition[] {
  if (!vertical || vertical === "all") {
    return [...LOTTIE_PRESETS];
  }

  return LOTTIE_PRESETS.filter((preset) => preset.vertical === vertical);
}
